import { supabase } from '@/integrations/supabase/client';

export interface RagMetrics {
  id: string;
  region: string;
  status: 'success' | 'error';
  articles_fetched: number;
  articles_after_dedup: number;
  sources_verified: number;
  sources_accessible: number;
  clusters_identified: number;
  total_latency_ms: number;
  search_latency_ms: number;
  verification_latency_ms: number;
  clustering_latency_ms: number;
  generation_latency_ms: number;
  storage_latency_ms: number;
  metadata: {
    warnings?: string[];
    rejectedDomains?: string[];
    model?: string;
    temperature?: number;
  };
  created_at: string;
}

export interface RegionStats {
  region: string;
  totalRuns: number;
  successRate: number;
  avgSourcesFound: number;
  avgExecutionTime: number;
  lastRun: string;
  rejectedDomains: string[];
}

export class RagMetricsService {
  static async getMetrics(days: number = 30): Promise<RagMetrics[]> {
    const { data, error } = await supabase
      .from('rag_generation_metrics')
      .select('*')
      .gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data as any) || [];
  }

  static async getRegionStats(days: number = 30): Promise<RegionStats[]> {
    const metrics = await this.getMetrics(days);
    
    const regionMap = new Map<string, RagMetrics[]>();
    metrics.forEach(m => {
      if (!regionMap.has(m.region)) {
        regionMap.set(m.region, []);
      }
      regionMap.get(m.region)!.push(m);
    });

    return Array.from(regionMap.entries()).map(([region, regionMetrics]) => {
      const totalRuns = regionMetrics.length;
      const successCount = regionMetrics.filter(m => m.status === 'success').length;
      const avgSourcesFound = regionMetrics.reduce((sum, m) => sum + m.sources_accessible, 0) / totalRuns;
      const avgExecutionTime = regionMetrics.reduce((sum, m) => sum + m.total_latency_ms, 0) / totalRuns;
      const lastRun = regionMetrics[0]?.created_at || '';
      
      const rejectedDomains = new Set<string>();
      regionMetrics.forEach(m => {
        m.metadata?.rejectedDomains?.forEach(d => rejectedDomains.add(d));
      });

      return {
        region,
        totalRuns,
        successRate: (successCount / totalRuns) * 100,
        avgSourcesFound,
        avgExecutionTime,
        lastRun,
        rejectedDomains: Array.from(rejectedDomains),
      };
    }).sort((a, b) => b.totalRuns - a.totalRuns);
  }

  static async getTimeSeriesData(days: number = 30) {
    const metrics = await this.getMetrics(days);
    
    const dailyData = new Map<string, {
      date: string;
      successCount: number;
      errorCount: number;
      avgSourcesFound: number;
      avgExecutionTime: number;
    }>();

    metrics.forEach(m => {
      const date = new Date(m.created_at).toISOString().split('T')[0];
      if (!dailyData.has(date)) {
        dailyData.set(date, {
          date,
          successCount: 0,
          errorCount: 0,
          avgSourcesFound: 0,
          avgExecutionTime: 0,
        });
      }
      const day = dailyData.get(date)!;
      if (m.status === 'success') {
        day.successCount++;
        day.avgSourcesFound += m.sources_accessible;
        day.avgExecutionTime += m.total_latency_ms;
      } else {
        day.errorCount++;
      }
    });

    return Array.from(dailyData.values())
      .map(day => ({
        ...day,
        avgSourcesFound: day.successCount > 0 ? day.avgSourcesFound / day.successCount : 0,
        avgExecutionTime: day.successCount > 0 ? day.avgExecutionTime / day.successCount : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  static async getRejectedDomainsStats(days: number = 30) {
    const metrics = await this.getMetrics(days);
    
    const domainCount = new Map<string, number>();
    metrics.forEach(m => {
      m.metadata?.rejectedDomains?.forEach(domain => {
        domainCount.set(domain, (domainCount.get(domain) || 0) + 1);
      });
    });

    return Array.from(domainCount.entries())
      .map(([domain, count]) => ({ domain, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }

  static async getRecentAlerts(limit: number = 10) {
    const { data, error } = await supabase
      .from('rag_alerts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }
}
